/**
 * Centralized z-index scale.
 *
 * Layers are spaced by 100 to leave room for fine-grained stacking
 * within a layer. Use the Tailwind arbitrary-value syntax in JSX:
 *   className={`z-[${Z.DROPDOWN}]`}
 *
 * Or reference directly in inline styles:
 *   style={{ zIndex: Z.DROPDOWN }}
 *
 * Scale:
 *   CONTENT      (0)   — default document flow
 *   STICKY      (100)  — sticky headers, pinned elements
 *   CHAT_TOGGLE (150)  — the desktop chat edge handle
 *   DROPDOWN    (200)  — dropdowns, popovers, tooltips
 *   OVERLAY     (300)  — modal backdrops, mobile nav overlays
 *   MODAL       (400)  — modal content, mobile nav panels
 *   TOAST       (500)  — toast notifications, snackbars
 *
 * **Portaling to `document.body` is choosing a z-index.** A portaled overlay is a
 * *sibling* of any open modal, not a child, so paint order is decided by z-index
 * alone — anything at or below `MODAL` is invisible in every modal-hosted surface.
 * If the overlay can ever render inside a modal, use `Z.MODAL + 50`, and check
 * whether it raises a dialog of its own (that dialog is now underneath it, so the
 * overlay must hide while it is open). See
 * `docs/lessons-learned/a-body-portaled-overlay-must-outrank-z-modal.md`.
 */
export const Z = {
  /**
   * Behind all content — decorative backdrops (e.g. the seasonal snowdrift
   * layer). Sits above the page background but below normal document flow, so
   * opaque content/cards always render over it.
   */
  BACKDROP: -1,
  /** Default document flow */
  CONTENT: 0,
  /** Sticky headers, pinned rows, sub-nav scroll shadows */
  STICKY: 100,
  /**
   * The desktop chat edge handle (right edge of the content column).
   * Sits above sticky page headers so it is never hidden behind them, but
   * below dropdowns/popovers so those still open over it.
   */
  CHAT_TOGGLE: 150,
  /**
   * Ambient full-screen decoration (e.g. seasonal snow). Blankets all app
   * chrome — sidebar, headers, chat toggle — but stays below dropdowns so
   * popovers/menus, modals, and toasts still render over it. Always
   * `pointer-events: none`, so it never intercepts interaction.
   */
  AMBIENT: 160,
  /** Dropdowns, popovers, tooltips, typeaheads */
  DROPDOWN: 200,
  /** Modal backdrops, mobile nav overlay backgrounds */
  OVERLAY: 300,
  /** Modal content, mobile nav panels, full-screen overlays */
  MODAL: 400,
  /** Toast notifications, snackbars, undo bars */
  TOAST: 500,
} as const;
